"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 227: Hexin "1200-in-1" multicart
 * ------------------------------------------------------------------ *
 * Address-latch multicart board (PCB 810449-C-A1 and friends): up to
 * 1 MB of PRG-ROM in 16 KB banks and 8 KB of unbanked CHR-RAM. The
 * board decodes the WRITE ADDRESS itself, not the data written: every
 * write to $8000-$FFFF re-latches a full configuration from the
 * address lines, so the value is irrelevant.
 *
 *   [A~1... .pL?  OQQP PpqP]  (diagram bits shown from A9 down)
 *
 * Emulated configuration bits taken from the address:
 *   bit 0 (S)  : 32 KB mode instead of two 16 KB windows
 *   bit 1      : horizontal mirroring when set, vertical when clear
 *   bits 2-6   : 5-bit PRG bank number ("inner", 16 KB units)
 *   bit 7 (M)  : 32 KB modes (NROM-like) instead of UNROM-like modes
 *   bit 8 (O)  : outer bank bit, adds 32 to the bank number
 *   bit 9 (L)  : in UNROM-like modes the $C000 window follows the
 *                bank number's low bits instead of being masked down
 *
 * resulting bank layout (bank = 16 KB units):
 *   M=1: S=1  -> one 32 KB bank (bank...even) at $8000-$FFFF
 *        S=0  -> 16 KB bank mirrored at $8000 and $C000
 *   M=0: S=0  -> $8000 = bank, $C000 = L ? bank|7 : bank&0x38
 *        S=1  -> $8000 = bank&0x3E, $C000 = L ? bank|7 : bank&0x38
 *
 * CHR is 8 KB of cartridge RAM, so the PPU's own pattern RAM serves as
 * CHR-RAM and nothing banks it. (The Waixing/Yancheng RPG variant with
 * battery WRAM -- Nestopia submapper 0 -- is not modelled here.)
 *
 * Used by 1200-in-1 (J) [p1] and other Chinese multicarts.
 * Reference: https://www.nesdev.org/wiki/INES_Mapper_227
 * Mesen: Core/Mapper227.h
 */

function register(Mappers) {
  Mappers[227] = function (nes) {
    this.nes = nes;
  };

  Mappers[227].prototype = new Mappers[0]();

  Mappers[227].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);
    // The last written address carries the whole configuration.
    this.regAddr = 0;
  };

  Mappers[227].prototype.write = function (address, value) {
    if (address >= 0x8000) {
      this.regAddr = address;
      this.updateBanks();
      return;
    }

    Mappers[0].prototype.write.apply(this, arguments);
  };

  Mappers[227].prototype.updateBanks = function () {
    var addr = this.regAddr;
    var prgBank = ((addr >> 2) & 0x1f) | ((addr & 0x100) >> 3);
    var sFlag = (addr & 0x01) === 0x01;
    var lFlag = (addr & 0x200) === 0x200;
    var prgMode = (addr & 0x80) === 0x80;

    if (prgMode) {
      if (sFlag) {
        // One 32 KB bank at $8000-$FFFF: even bank number + its follower.
        this.loadRomBank(prgBank & 0xfe, 0x8000);
        this.loadRomBank((prgBank & 0xfe) + 1, 0xc000);
      } else {
        // 16 KB NROM-128: one bank mirrored at $8000 and $C000.
        this.loadRomBank(prgBank, 0x8000);
        this.loadRomBank(prgBank, 0xc000);
      }
    } else {
      if (sFlag) {
        this.loadRomBank(prgBank & 0x3e, 0x8000);
      } else {
        this.loadRomBank(prgBank, 0x8000);
      }
      this.loadRomBank(lFlag ? prgBank | 0x07 : prgBank & 0x38, 0xc000);
    }

    // Per-write mirroring: bit 1 of the address, H when set.
    this.setMirroring(
      (addr & 0x02) === 0x02
        ? this.nes.rom.HORIZONTAL_MIRRORING
        : this.nes.rom.VERTICAL_MIRRORING,
    );
  };

  Mappers[227].prototype.loadROM = function () {
    if (!this.nes.rom.valid || this.nes.rom.romCount < 1) {
      throw new Error("Hexin 1200-in-1: Invalid ROM! Unable to load.");
    }

    this.reset();
    // Power-on state as the board latches at reset (bank 0 mirrored,
    // vertical mirroring): updateBanks() derives it from regAddr = 0.
    this.updateBanks();
    this.resetIrqHandler();
  };

  Mappers[227].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.regAddr = this.regAddr;
    return s;
  };

  Mappers[227].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.regAddr = s.regAddr;
    this.updateBanks();
  };
}

module.exports = { register: register };
